<template>
    <section class="max-w-screen-xl mx-auto px-4 mb-20 relative">
        <!-- Cross Lines Background -->
        <div class="absolute inset-0 opacity-30 pointer-events-none">
            <div class="grid-background"></div>
        </div>

        <!-- Content with relative positioning to appear above background -->
        <div class="relative z-10">
            <div class="mb-10">
                <div class="max-w-max mx-auto relative mb-8">
                    <NuxtImg src="/images/circuit.png" width="298" height="180" alt="Shopping Bag"
                        class="w-32 sm:w-40 md:w-56 lg:w-72 xl:w-[298px] h-auto" loading="lazy" />
                    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                 flex items-center justify-center 
                 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 
                 rounded-xl sm:rounded-2xl bg-[image:var(--primary-gradient)]">
                        <NsLogo name="NsThirdPartyToolsLogo" class="w-5 sm:w-6 md:w-8 lg:w-10 h-auto"/>
                    </div>
                </div>

                <!-- Heading -->
                <h2 class="text-lg sm:text-xl md:text-2xl lg:text-3xl text-[var(--text-secondary)] 
               text-center font-bold -mt-3 sm:-mt-4 md:-mt-6 lg:-mt-7 mb-4 px-2">
                    Stay Ahead with Competitor Insights
                </h2>
                <p class="text-sm sm:text-base md:text-lg lg:text-xl text-[var(--text-gray)] 
               max-w-4xl text-center mx-auto mb-6 sm:mb-8 md:mb-10 px-2 leading-relaxed">
                    Discover how your platform compares with competitors. Our analysis highlights unique advantages,
                    identifies growth opportunities, and helps you make informed decisions to stay ahead in the market
                </p>

                <Button unstyled label="Learn More" iconPos="left" class="group !rounded-xl !px-4 sm:!px-5 
         bg-[image:var(--primary-gradient)] hover:bg-[image:var(--primary-gradient-reverse)]
         transition-all duration-1000 ease-in-out
         !py-2.5 sm:!py-3 font-semibold
         text-[var(--text-secondary)] 
         flex items-center justify-center gap-2 sm:gap-3 
         w-full max-w-[280px] sm:w-auto text-sm sm:text-base overflow-hidden mx-auto">

                        <span class="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 
             rounded-full bg-white 
             transform transition-all duration-1000 ease-in-out 
             group-hover:translate-x-28">
                            <i class="pi pi-angle-right text-green-500 font-bold text-sm sm:text-lg"></i>
                        </span>
                        <span class="transition-all duration-1000 ease-in-out group-hover:-translate-x-1/2">
                            Learn More
                        </span>
                    </Button>
            </div>

            <!-- Mobile Layout (below md) -->
            <div class="block md:hidden">
                <!-- Central Logo -->
                <div
                    class="w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-[#2A2A48] flex flex-col items-center justify-center gap-2 mx-auto mb-8">
                    <NuxtImg src="/logos/nuxtstore-logo.svg" alt="Nuxt Store Logo" width="40" height="40"
                        class="sm:w-12 sm:h-12" loading="lazy" />
                    <p class="text-[var(--text-gray)] text-base sm:text-lg">Nuxt<span
                            class="font-bold text-[var(--text-secondary)]">Store</span>
                    </p>
                </div>

                <!-- Mobile Store Selection -->
                <div class="grid grid-cols-2 gap-4 mb-8">
                    <div v-for="(store, index) in mobileStores" :key="`mobile-${index}`" class="cursor-pointer"
                        @click="activeBox = index + 1">
                        <div class="text-center">
                            <div class="w-20 h-16 sm:w-24 sm:h-20 border rounded-xl mb-2 mx-auto bg-[var(--background-btn)] flex items-center justify-center transition-all duration-300"
                                :class="activeBox === index + 1 ? 'border-blue-500 shadow-lg scale-105' : 'border-transparent'">
                                <NsLogo :name="store.icon" class="w-6 h-6 sm:w-8 sm:h-8" />
                            </div>
                            <span class="text-[var(--text-gray)] text-sm sm:text-base">{{ store.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Mobile Features List -->
                <div class="bg-[var(--background-btn)] rounded-2xl p-6 space-y-4">
                    <h3 class="text-[var(--text-secondary)] font-semibold text-lg mb-4 text-center">
                        {{ mobileStores[activeBox - 1]?.name }} Features
                    </h3>
                    <div v-for="(item, index) in multiStoreLabels" :key="`mobile-label-${index}`"
                        class="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg">
                        <span class="text-[var(--text-gray)] text-sm sm:text-base">{{ item }}</span>
                        <!-- Dynamic Icon (Green Tick or Red Cross) -->
                        <div class="relative">
                            <!-- Green Tick -->
                            <div v-if="getFeatureStatus(item, activeBox)" class="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center 
                                        animate-pulse-scale shadow-lg">
                                <svg class="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-white animate-check-draw"
                                    viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        fill="currentColor" />
                                </svg>
                                <!-- Green Ripple effect -->
                                <div class="absolute inset-0 bg-green-400 rounded-full animate-ripple opacity-40"></div>
                            </div>

                            <!-- Red Cross -->
                            <div v-else class="w-5 h-5 sm:w-6 sm:h-6 bg-red-500 rounded-full flex items-center justify-center 
                                        animate-pulse-scale-red shadow-lg">
                                <svg class="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-white animate-cross-draw"
                                    viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                        fill="currentColor" />
                                </svg>
                                <!-- Red Ripple effect -->
                                <div class="absolute inset-0 bg-red-400 rounded-full animate-ripple opacity-40"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Desktop Layout (md and above) -->
            <div class="hidden md:flex items-center justify-center w-full gap-8 lg:gap-16 xl:gap-[212px]">
                <!-- Large Circle -->
                <div
                    class="w-36 h-36 lg:w-44 lg:h-44 rounded-full bg-[#2A2A48] flex flex-col items-center justify-center gap-2">
                    <NuxtImg src="/logos/nuxtstore-logo.svg" alt="Nuxt Store Logo" width="50" height="50"
                        class="lg:w-[60px] lg:h-[60px]" loading="lazy" />
                    <p class="text-[var(--text-gray)] text-lg lg:text-xl">Nuxt<span
                            class="font-bold text-[var(--text-secondary)]">Store</span>
                    </p>
                </div>

                <!-- Connected multi store boxes -->
                <div class="flex flex-col gap-8 lg:gap-[50px] shrink-0 mt-4 relative">
                    <!-- Box 1 -->
                    <div class="relative cursor-pointer transition-all duration-300 hover:scale-105"
                        @click="activeBox = 1">
                        <div class="text-center">
                            <div class="w-20 h-16 lg:w-[90px] lg:h-[65px] border rounded-xl mb-2 mx-auto bg-[var(--background-btn)] flex items-center justify-center transition-all duration-300"
                                :class="activeBox === 1 ? 'border-blue-500 shadow-lg' : 'border-transparent'">
                                 <NsLogo name="NsVaahStore" class="w-6 h-6 lg:w-8 lg:h-8" />
                            </div>
                            <span class="text-[var(--text-gray)] text-sm lg:text-base">Vaah Store</span>
                        </div>

                        <!-- Show only if selected -->
                        <template v-if="activeBox === 1">
                            <NuxtImg src="/images/competitor-insights/vaah-store-left-line-1.png" width="213"
                                height="234.5" alt="line 1" densities="x1"
                                class="absolute min-w-max top-1/3 right-full hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-multivendor.png" width="214"
                                height="44.5" alt="multivendor" densities="x1"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-multilingual.png" width="214"
                                height="120.5" alt="multilingual" densities="x1"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-multicurrency.png" width="214"
                                height="196.5" alt="multicurrency" densities="x1"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-wishlist.png" width="214" height="272.5"
                                densities="x1" alt="wishlist"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-brandstore.png" densities="x1"
                                width="214" height="348.5" alt="brandstore"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/vaahstore-dropship.png" densities="x1" width="214"
                                height="424.5" alt="dropship"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                        </template>
                    </div>

                    <!-- Box 2 -->
                    <div class="relative cursor-pointer transition-all duration-300 hover:scale-105"
                        @click="activeBox = 2">
                        <div class="text-center">
                            <div class="w-20 h-16 lg:w-[90px] lg:h-[65px] border rounded-xl mb-2 mx-auto bg-[var(--background-btn)] flex items-center justify-center transition-all duration-300"
                                :class="activeBox === 2 ? 'border-blue-500 shadow-lg' : 'border-transparent'">
                                 <NsLogo name="NsMagento" class="w-6 h-6 lg:w-8 lg:h-8" />
                            </div>
                            <span class="text-[var(--text-gray)] text-sm lg:text-base">Magneto</span>
                        </div>

                        <template v-if="activeBox === 2">
                            <NuxtImg src="/images/competitor-insights/magneto-line-1.png" width="213" height="87.5"
                                densities="x1" alt="line 1"
                                class="absolute min-w-max top-1/3 right-full hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-mutivendor.png" width="214" height="102.5"
                                densities="x1" alt="multivendor"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-multilingual.png" width="214"
                                height="26.5" densities="x1" alt="multilingual"
                                class="absolute left-full top-[5px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-multicurrency.png" width="214"
                                height="49.5" densities="x1" alt="multicurrency"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-wishlist.png" width="214" height="125.5"
                                densities="x1" alt="wishlist"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-brandstore.png" width="214" height="201.5"
                                densities="x1" alt="brandstore"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/magneto-dropship.png" width="214" height="277.5"
                                densities="x1" alt="dropship"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                        </template>
                    </div>

                    <!-- Box 3 -->
                    <div class="relative cursor-pointer transition-all duration-300 hover:scale-105"
                        @click="activeBox = 3">
                        <div class="text-center">
                            <div class="w-20 h-16 lg:w-[90px] lg:h-[65px] border rounded-xl mb-2 mx-auto bg-[var(--background-btn)] flex items-center justify-center transition-all duration-300"
                                :class="activeBox === 3 ? 'border-blue-500 shadow-lg' : 'border-transparent'">
                                 <NsLogo name="NsWoo" class="w-6 h-6 lg:w-8 lg:h-8" />
                            </div>
                            <span class="text-[var(--text-gray)] text-sm lg:text-base">Woo</span>
                        </div>

                        <template v-if="activeBox === 3">
                            <NuxtImg src="/images/competitor-insights/woo-line-1.png" width="213" height="59.5"
                                densities="x1" alt="line 1"
                                class="absolute min-w-max bottom-2/3 right-full hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-multivendor.png" width="214" height="249.5"
                                densities="x1" alt="multivendor"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-multilingual.png" width="214" height="173.5"
                                densities="x1" alt="multilingual"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-multicurrency.png" width="214" height="97.5"
                                densities="x1" alt="multicurrency"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-wishlist.png" width="214" height="21.5"
                                densities="x1" alt="wishlist"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-brandstore.png" width="214" height="54.5"
                                densities="x1" alt="brandstore"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/woo-dropship.png" width="214" height="130.5"
                                densities="x1" alt="dropship"
                                class="absolute left-full top-1/3 min-w-max hidden lg:block" loading="lazy" />
                        </template>
                    </div>

                    <!-- Box 4 -->
                    <div class="relative cursor-pointer transition-all duration-300 hover:scale-105"
                        @click="activeBox = 4">
                        <div class="text-center">
                            <div class="w-20 h-16 lg:w-[90px] lg:h-[65px] border rounded-xl mb-2 mx-auto flex items-center justify-center bg-[var(--background-btn)] transition-all duration-300"
                                :class="activeBox === 4 ? 'border-blue-500 shadow-lg' : 'border-transparent'">
                                 <NsLogo name="NsShopify" class="w-6 h-6 lg:w-8 lg:h-8" />
                            </div>
                            <span class="text-[var(--text-gray)] text-sm lg:text-base">Shopify</span>
                        </div>

                        <template v-if="activeBox === 4">
                            <NuxtImg src="/images/competitor-insights/shopify-line-1.png" height="206.5" width="213"
                                densities="x1" alt="line 1"
                                class="absolute min-w-max bottom-2/3 right-full hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-multivendor.png" width="214"
                                height="396.5" densities="x1" alt="multivendor"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-multilingual.png" width="214"
                                height="320.5" densities="x1" alt="multilingual"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-multicurrency.png" width="214"
                                height="244.5" densities="x1" alt="multicurrency"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-wishlist.png" width="214" height="168.5"
                                densities="x1" alt="wishlist"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-brandstore.png" width="214" height="92.5"
                                densities="x1" alt="brandstore"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                            <NuxtImg src="/images/competitor-insights/shopify-dropship.png" width="214" height="16.5"
                                densities="x1" alt="dropship"
                                class="absolute left-full bottom-[62px] min-w-max hidden lg:block" loading="lazy" />
                        </template>
                    </div>
                </div>

                <!-- Right multi store labels with dynamic animated icons (Desktop only, hidden on smaller screens) -->
                <div class="hidden lg:flex flex-col gap-[52px] mt-3">
                    <div v-for="(item, index) in multiStoreLabels" :key="`desktop-label-${index}`"
                        class="flex items-center gap-3">
                        <!-- Dynamic Icon (Green Tick or Red Cross) -->
                        <div class="relative">
                            <!-- Green Tick -->
                            <div v-if="getFeatureStatus(item, activeBox)" class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center 
                                        animate-pulse-scale shadow-lg">
                                <svg class="w-3.5 h-3.5 text-white animate-check-draw" viewBox="0 0 20 20" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        fill="currentColor" />
                                </svg>
                                <!-- Green Ripple effect -->
                                <div class="absolute inset-0 bg-green-400 rounded-full animate-ripple opacity-40"></div>
                            </div>

                            <!-- Red Cross -->
                            <div v-else class="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center 
                                        animate-pulse-scale-red shadow-lg">
                                <svg class="w-3.5 h-3.5 text-white animate-cross-draw" viewBox="0 0 20 20" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                        fill="currentColor" />
                                </svg>
                                <!-- Red Ripple effect -->
                                <div class="absolute inset-0 bg-red-400 rounded-full animate-ripple opacity-40"></div>
                            </div>
                        </div>

                        <span class="text-[var(--text-gray)] text-base">{{ item }}</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const NsLogo = defineAsyncComponent(() => import('./NsLogo.vue'));

const activeBox = ref(1) // Default selected box

const multiStoreLabels = ref([
    'Multi Vendor',
    'Multi Lingual',
    'Multi Currency',
    'Wishlist',
    'Brand Store',
    'Dropship'
])

// Mobile store data
const mobileStores = ref([
    { name: 'Vaah Store', icon: 'NsVaahStore' },
    { name: 'Magneto', icon: 'NsMagento' },
    { name: 'Woo', icon: 'NsWoo' },
    { name: 'Shopify', icon: 'NsShopify' }
])

// Define feature availability for each store
const storeFeatures = {
    1: { // Vaah Store - all features available (all ticks)
        'Multi Vendor': true,
        'Multi Lingual': true,
        'Multi Currency': true,
        'Wishlist': true,
        'Brand Store': true,
        'Dropship': true
    },
    2: { // Magneto - multivendor and dropship tick, others cross
        'Multi Vendor': true,
        'Multi Lingual': false,
        'Multi Currency': false,
        'Wishlist': false,
        'Brand Store': false,
        'Dropship': true
    },
    3: { // Woo - multi vendor, multilingual and dropship tick, others cross
        'Multi Vendor': true,
        'Multi Lingual': true,
        'Multi Currency': false,
        'Wishlist': false,
        'Brand Store': false,
        'Dropship': true
    },
    4: { // Shopify - multi vendor, wishlist and dropship tick, others cross
        'Multi Vendor': true,
        'Multi Lingual': false,
        'Multi Currency': false,
        'Wishlist': true,
        'Brand Store': false,
        'Dropship': true
    }
}

// Function to get feature status based on store and feature
const getFeatureStatus = (feature, storeId) => {
    return storeFeatures[storeId] ? storeFeatures[storeId][feature] : false
}
</script>

<style scoped>
.grid-background {
    width: 100%;
    height: 100%;
    background-image:
        linear-gradient(rgba(17, 42, 29, 1) 1.30px, transparent 1.30px),
        linear-gradient(90deg, rgba(17, 42, 29, 1) 1.30px, transparent 1.30px);
    background-size: 60px 60px;
    background-position: 0 0, 0 0;
}

@media (min-width: 768px) {
    .grid-background {
        background-size: 80px 80px;
    }
}

/* Green Tick Animations */
@keyframes pulse-scale {

    0%,
    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
    }
}

.animate-pulse-scale {
    animation: pulse-scale 2s infinite;
}

/* Red Cross Animations */
@keyframes pulse-scale-red {

    0%,
    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
}

.animate-pulse-scale-red {
    animation: pulse-scale-red 2s infinite;
}

/* Check mark draw animation */
@keyframes check-draw {
    0% {
        stroke-dasharray: 0 100;
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    100% {
        stroke-dasharray: 100 100;
        opacity: 1;
    }
}

.animate-check-draw {
    animation: check-draw 1.5s ease-in-out infinite;
}

/* Cross draw animation */
@keyframes cross-draw {
    0% {
        transform: scale(0.8);
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.animate-cross-draw {
    animation: cross-draw 1.5s ease-in-out infinite;
}

/* Ripple effect animation */
@keyframes ripple {
    0% {
        transform: scale(1);
        opacity: 0.4;
    }

    100% {
        transform: scale(2);
        opacity: 0;
    }
}

.animate-ripple {
    animation: ripple 2s infinite;
}

/* Smooth transitions for interactive elements */
.cursor-pointer {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.cursor-pointer:hover {
    transform: translateY(-2px);
}

.cursor-pointer:active {
    transform: translateY(0);
}
</style> 